import {
    OpenAuditActivityEvent, OpenFindingForActivityEvent,
    SwitchToFindingTabsEvent, WorkbenchPlugin,
    WorkbenchPluginId, WorkbenchViewConfig, WorkbenchViewKind
} from '../../shared/types';
import {Injectable} from '@angular/core';
import {EMPTY, Observable, of, Subscription} from 'rxjs';
import {catchError, distinctUntilChanged, filter, map, switchMap, take} from 'rxjs/operators';
import {AuditNavigatorViewComponent} from './audit-navigator-view.component';
import {WorkbenchCommandRegistry} from '../../shared/services/workbench-command-registry.service';
import {WorkbenchLayoutManager} from '../../shared/services/workbench-layout-manager.service';
import {InspectorService} from '../../shared/services/inspector.service';
import {WorkbenchAuditService} from '../../shared/services/workbench-audit.service';
import {WorkbenchContextService} from '../../shared/services/workbench-context.service';
import {audit} from '@webmate-web-core/shared/icons';
import {AuditActivityId, FindingId} from '@webmate-web-core/shared/types/ids';

// @WorkbenchPlugin
@Injectable()
export class AuditPlugin extends WorkbenchPlugin {
    id: WorkbenchPluginId = 'audit';
    views: WorkbenchViewKind[];

    static readonly AuditNavigatorViewId = 'audit.view.audit-navigator';
    static readonly ViewContainerId = 'audit.viewcontainer';

    private activeAuditSubscription?: Subscription;

    constructor(private readonly commandRegistry: WorkbenchCommandRegistry,
                private readonly inspectorService: InspectorService,
                private readonly workbenchAuditService: WorkbenchAuditService,
                private readonly workbenchContextService: WorkbenchContextService,
                readonly layoutManager: WorkbenchLayoutManager) {
        super(layoutManager);
        this.views = [
            {
                id: AuditPlugin.AuditNavigatorViewId,
                component: AuditNavigatorViewComponent,
                name: 'Audit Navigator',
                defaultViewContainer: AuditPlugin.ViewContainerId,
                visibility: 'audit',
            }
        ];
    }

    onWorkbenchDestroy(): void {
        this.activeAuditSubscription?.unsubscribe();
    }

    onWorkbenchInit(): Observable<void> {
        this.layoutManager.registerViewContainer({
            type: 'NormalViewContainer',
            id: AuditPlugin.ViewContainerId,
            name: 'Audit',
            icon: {
                type: 'svg',
                path: '../../assets/img/audit.svg',
            },
            allowedPanels: ['left-top', 'left-bottom'],
            defaultPanel: 'left-top',
            displayMode: 'stacked',
            toolbarCommandId: 'audit:toggleViewContainer'
        });

        this.commandRegistry.registerCommand(
            'audit:toggleViewContainer',
            {
                name: 'Audit View',
                icon: {
                    type: 'faIcon',
                    instance: audit,
                },
                description: 'Toggle Audit View',
                statusBindings: {
                    'active': {
                        'type': 'store',
                        'storeKey': 'viewcontainers.audit.viewcontainer.status.visible',
                    }
                }
            },
            () => {
                this.layoutManager.toggleViewContainer('audit.viewcontainer', true);
            }
        )

        this.commandRegistry.registerCommand(
            "audit:openActivity",
            {
                name: 'Open specific Activity in the Inspector',
            },
            (params: any) => {
                this.workbenchAuditService.setActiveActivity(params);
                this.inspectorService.changeItem('Audit');
                const event: OpenAuditActivityEvent = {type: 'OpenAuditActivity', activityID: params};
                setTimeout(() => this.inspectorService.raiseInspectorEvent(event));
            }
        )

        this.commandRegistry.registerCommand(
            "audit:openFindingForActivity",
            {
                name: 'Open specific Finding in the Inspector',
            },
            (params: {findingId: FindingId, activityId: AuditActivityId}) => {
                this.workbenchAuditService.setActiveActivity(params.activityId);
                this.inspectorService.changeItem('Audit');
                const event: OpenFindingForActivityEvent = {type: 'OpenFindingForActivityEvent', activityId: params.activityId, findingId: params.findingId};
                setTimeout(() => this.inspectorService.raiseInspectorEvent(event));
            }
        )

        this.commandRegistry.registerCommand(
            "audit:switchToFindingTab",
            {
                name: 'Switches to the Finding tab of the activity',
            },
            () => {
                const event: SwitchToFindingTabsEvent = {type: 'SwitchToFindingTab'};
                this.inspectorService.raiseInspectorEvent(event);
            }
        )

        this.commandRegistry.registerCommand(
            "audit:focusActivity",
            {
                name: 'Scrolls to specific Activity inside the Audit Navigator',
            },
            (params: any) => {
                this.workbenchAuditService.focusActivity(params[0]);
            }
        )

        // When an audit is opened (initially or by switching audits) and an active activity is restored
        // from persistence, open that activity in the inspector just like a user selection would. We wait
        // until the audit data is loaded and the activity actually exists before issuing the command, so
        // the inspector has the data it needs to render (and to avoid races during workbench startup).
        // If the audit loads but the restored activity is no longer there, we clear the stale reference so
        // the workbench does not keep pointing at an activity that is gone. Transient load failures are left
        // untouched (caught to EMPTY) so the reference survives a retry.
        this.activeAuditSubscription = this.workbenchContextService.subscribeWorkbenchContext$().pipe(
            map(context => context.auditContext),
            distinctUntilChanged((previous, current) => previous?.activeAudit === current?.activeAudit),
            switchMap(auditContext => {
                const auditId = auditContext?.activeAudit;
                const restoredActivity = auditContext?.activeAuditActivity;
                if (!auditId || !restoredActivity) {
                    return EMPTY;
                }
                return this.workbenchAuditService.getOrCreateAuditSession(auditId).getAuditInfo$().pipe(
                    // The session starts with an empty placeholder; wait until the real audit data is loaded.
                    filter(auditInfo => (auditInfo.assessmentTasks?.length ?? 0) > 0),
                    take(1),
                    map(auditInfo => {
                        // Resolve by the stable (testObject, spec) pair rather than the persisted id, which can
                        // be stale (e.g. a flyweight activity that has since been reified gets a different id).
                        const match = (auditInfo.assessmentTasks || []).find(task =>
                            task.testObjectId === restoredActivity.testObjectId && task.assessedSpec === restoredActivity.assessedSpec);
                        return {matchedActivityId: match?.id, restoredActivity};
                    }),
                    catchError(() => EMPTY)
                );
            })
        ).subscribe(({matchedActivityId, restoredActivity}) => {
            if (matchedActivityId) {
                this.commandRegistry.executeCommand('audit:openActivity', matchedActivityId);
                return;
            }
            // Bail if the active activity changed meanwhile (e.g. the user selected a different one).
            const current = this.workbenchContextService.getCurrentWorkbenchContext()?.auditContext?.activeAuditActivity;
            if (current?.testObjectId === restoredActivity.testObjectId && current?.assessedSpec === restoredActivity.assessedSpec) {
                this.workbenchContextService.setContextActiveAuditActivity(undefined);
            }
        });

        return of(undefined);
    }

}
